import sanitizeHtml from 'sanitize-html';

interface GeoLocation {
	longitude: string;
	latitude: string;
}

interface Address {
	city: string;
	state: string;
	zip: string;
	addressLines: string[];
}

interface NameDetails {
	name: string;
	label: string;
	shortName: string;
	webLabel: string;
}

interface Center {
	uuid: string;
	name: NameDetails;
	geoLocation: GeoLocation;
	address: Address;
	phone: string;
	permalink: string;
}

interface LastSearch {
	type: string;
	zipcode?: string;
	lat?: number;
	lng?: number;
}

/**
 * Google Maps Handler
 */
class GoogleMapHandler {
	private map: google.maps.Map | null = null;
	private markers: google.maps.marker.AdvancedMarkerElement[] = [];
	private openInfoWindow: google.maps.InfoWindow | null = null;
	private mapElement = document.querySelector<HTMLElement>('.block-location-map__map');
	private resultsContainer = document.querySelector<HTMLElement>('.block-location-map__results');
	private postalCodeForm = document.querySelector<HTMLFormElement>('.block-location-map__form');
	private postalCodeInput =
		this.postalCodeForm?.querySelector<HTMLInputElement>("input[name='zipcode']");
	private findMeButton = document.querySelector<HTMLElement>('.block-location-map__find-me');
	private errorContainer = document.querySelector<HTMLElement>('.block-location-map__errors');
	private lastSearch: LastSearch | boolean = false;
	private lastSearchKey = 'lastMapSearch';
	private hasSearched = false;
	private initialMapCenter = { lat: 38.1520477, lng: -97.1542037 };
	private initialMapZoom = 3.5;

	/**
	 * Constructor.
	 *
	 * Initiate map and setup UI listeners.
	 *
	 * @return void If no mapElement.
	 */
	constructor() {
		window.dataLayer = window.dataLayer || [];

		if (!window.googleMapId) {
			return;
		}
		if (!this.mapElement) {
			return;
		}

		this.map = new google.maps.Map(this.mapElement, {
			center: this.initialMapCenter,
			zoom: this.initialMapZoom,
			mapId: sanitizeText(window.googleMapId),
			mapTypeId: google.maps.MapTypeId.ROADMAP,
			disableDefaultUI: true,
			zoomControl: true,
			streetViewControl: false,
			mapTypeControl: false,
			fullscreenControl: false,
			scaleControl: false,
		});

		this.setupFormListener();
		this.setupFindMeListener();
		this.initialAutoSearch();
	}

	/**
	 * Zipcode form submit listeners
	 *
	 * Capture zipcode value on form submit and fetch locations.
	 * Watches the zipcode field for format errors after first submission.
	 */
	private setupFormListener(): void {
		if (this.postalCodeForm) {
			this.postalCodeForm.addEventListener('submit', (event) => {
				event.preventDefault();
				if (this.postalCodeForm && this.postalCodeInput) {
					const sanitizedPostalCodeInput = sanitizeText(this.postalCodeInput.value);
					if (this.isValidZipcode(sanitizedPostalCodeInput)) {
						this.clearErrors();

						window.dataLayer.push({
							event: 'location_search',
							searchType: 'manual',
							zip: sanitizedPostalCodeInput,
						});

						this.fetchLocationsByZipcode(sanitizedPostalCodeInput);
					} else {
						this.displayError('Please input valid 5 digit US zipcode.');
					}
				}
			});
		}
		if (this.postalCodeInput) {
			this.postalCodeInput.addEventListener('keyup', () => {
				if (this.postalCodeInput && this.hasSearched) {
					const sanitizedPostalCodeInput = sanitizeText(this.postalCodeInput.value);
					if (this.isValidZipcode(sanitizedPostalCodeInput)) {
						this.clearErrors();
					} else {
						this.displayError('Please input valid 5 digit US zipcode.');
					}
				}
			});
		}
	}

	/**
	 * "Find me" button listener
	 *
	 * Request browser geolocation and fetch locations (often after consent).
	 */
	private setupFindMeListener(): void {
		if (this.findMeButton) {
			this.findMeButton.addEventListener('click', (event) => {
				event.preventDefault();
				if (navigator.geolocation) {
					navigator.geolocation.getCurrentPosition((position) => {
						const { latitude, longitude } = position.coords;

						window.dataLayer.push({
							event: 'location_search',
							searchType: 'manual',
							lat: latitude,
							lng: longitude,
						});

						this.fetchLocationsByCoordinates(latitude, longitude);
					});
				} else {
					// No error handling due to potential issues/conflicts/delays in UI.
				}
			});
		}
	}

	/**
	 * Initial search on page load.
	 *
	 * 1. Get the zipcode parameter, validate, and search for it.
	 * 2. If not set, autosearch the last successful input
	 * 3. If neither, do nothing.
	 */
	private initialAutoSearch() {
		const lastSearchedFromStorage = localStorage.getItem(this.lastSearchKey);
		const urlParams = new URLSearchParams(window.location.search);
		const initialZipcode = urlParams.get('zipcode');
		if (initialZipcode) {
			const sanitizedInitialZipcode = sanitizeText(initialZipcode);
			if (this.isValidZipcode(sanitizedInitialZipcode)) {
				if (this.postalCodeInput) {
					this.postalCodeInput.value = sanitizedInitialZipcode;
					this.postalCodeInput.dispatchEvent(new Event('input'));
				}

				window.dataLayer.push({
					event: 'location_search',
					searchType: 'auto_query',
					zip: sanitizedInitialZipcode,
				});

				this.fetchLocationsByZipcode(sanitizedInitialZipcode);
			}
		} else if (lastSearchedFromStorage) {
			const lastSearchedFromStorageJSON = JSON.parse(lastSearchedFromStorage);
			if (lastSearchedFromStorageJSON) {
				if (
					'zipcode' === lastSearchedFromStorageJSON.type &&
					lastSearchedFromStorageJSON.zipcode &&
					this.isValidZipcode(sanitizeText(lastSearchedFromStorageJSON.zipcode))
				) {
					const sanitizedZipcode = sanitizeText(lastSearchedFromStorageJSON.zipcode);
					if (this.postalCodeInput) {
						this.postalCodeInput.value = sanitizedZipcode;
						this.postalCodeInput.dispatchEvent(new Event('input'));
					}

					window.dataLayer.push({
						event: 'location_search',
						searchType: 'auto_cache',
						zip: sanitizedZipcode,
					});

					this.fetchLocationsByZipcode(sanitizedZipcode);
				} else if (
					'coordinates' === lastSearchedFromStorageJSON.type &&
					lastSearchedFromStorageJSON.lat &&
					lastSearchedFromStorageJSON.lng
				) {
					const sanitizedLatitude = parseFloat(
						sanitizeText(lastSearchedFromStorageJSON.lat)
					);
					const sanitizedLongitude = parseFloat(
						sanitizeText(lastSearchedFromStorageJSON.lng)
					);

					window.dataLayer.push({
						event: 'location_search',
						searchType: 'auto_cache',
						lat: sanitizedLatitude,
						lng: sanitizedLongitude,
					});

					this.fetchLocationsByCoordinates(sanitizedLatitude, sanitizedLongitude);
				}
			}
		}
	}

	/**
	 * Prepare API Request for zipcode
	 *
	 * @param zipcode US postal code for search.
	 */
	private fetchLocationsByZipcode(zipcode: string): void {
		const apiUrl = `/wp-json/ideal-api-router/v1/locations-by-zipcode?zipcode=${zipcode}`;

		this.hasSearched = true;

		this.lastSearch = {
			type: 'zipcode',
			zipcode,
		};
		localStorage.setItem(this.lastSearchKey, JSON.stringify(this.lastSearch));

		this.fetchLocations(apiUrl);
	}

	/**
	 * Prepare API Request for coordinates
	 *
	 * @param lat Latitude coordinate.
	 * @param lng Longitude coordinate.
	 */
	private fetchLocationsByCoordinates(lat: number, lng: number): void {
		const apiUrl = `/wp-json/ideal-api-router/v1/locations-by-coordinates?lat=${lat}&lng=${lng}`;

		this.lastSearch = {
			type: 'coordinates',
			lat,
			lng,
		};
		localStorage.setItem(this.lastSearchKey, JSON.stringify(this.lastSearch));

		this.fetchLocations(apiUrl);
	}

	/**
	 * Fetch locations given formatted API URL
	 *
	 * Update UI on success.
	 *
	 * @param apiUrl Prepared URL for search.
	 */
	private async fetchLocations(apiUrl: string): Promise<void> {
		this.clearErrors();
		this.resetUI();

		try {
			const response = await fetch(apiUrl);
			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'false');
			}
			const data: { center: Center; distanceInMiles: number }[] = await response.json();

			if (data.length > 0) {
				this.placeMarkers(data);
				this.buildCards(data);
			} else {
				this.recenterMap();
				this.displayError(
					'No locations found near you.<br /> Please scroll down and use our full <a href="#location-directory">directory</a>.'
				);

				if (this.lastSearch && typeof this.lastSearch === 'object') {
					if ('zipcode' === this.lastSearch.type && this.lastSearch.zipcode) {
						window.dataLayer.push({
							event: 'location_search_no_results',
							zipcode: this.lastSearch.zipcode,
						});
					} else if (
						'coordinates' === this.lastSearch.type &&
						this.lastSearch.lat &&
						this.lastSearch.lng
					) {
						window.dataLayer.push({
							event: 'location_search_no_results',
							lat: this.lastSearch.lat,
							lng: this.lastSearch.lng,
						});
					}
				}

				this.lastSearch = false;
				localStorage.removeItem(this.lastSearchKey);
			}
		} catch (error) {
			let errorMessage = error instanceof Error ? error.message : false;
			if (errorMessage === 'false' || !errorMessage) {
				errorMessage = false;
			}

			this.lastSearch = false;
			localStorage.removeItem(this.lastSearchKey);
			this.recenterMap();

			const errorMessageDisplay =
				errorMessage ||
				'We are experiencing an issue finding locations. We’re sorry!<br /> Please scroll down and use the <a href="#location-directory">directory</a> to find a location near you.';

			const errorMessageLog = errorMessage || 'Unknown API error.';

			if (typeof errorMessageDisplay === 'string') {
				this.displayError(errorMessageDisplay);
			}

			window.dataLayer.push({
				event: 'map_error',
				errorMessage: errorMessageLog,
			});
		}
	}

	/**
	 * Place markers on Google Map
	 *
	 * Build markers and infowindows.
	 * Zoom and reposition map to marker bounds.
	 *
	 * @param locations Locations for markering.
	 */
	private placeMarkers(locations: { center: Center; distanceInMiles: number }[]): void {
		if (!this.map) return;
		const bounds = new google.maps.LatLngBounds();
		locations.forEach(({ center }) => {
			const position = new google.maps.LatLng(
				parseFloat(center.geoLocation.latitude),
				parseFloat(center.geoLocation.longitude)
			);
			bounds.extend(position);

			if (window.googleMapMarkerDefault && window.googleMapMarkerHover) {
				const mapPin = document.createElement('img');
				mapPin.alt = 'Ideal Image Location Marker';
				mapPin.src = sanitizeText(window.googleMapMarkerDefault);

				const marker = new google.maps.marker.AdvancedMarkerElement({
					position,
					map: this.map,
					title: center.name.label,
					content: mapPin as HTMLImageElement,
				});

				mapPin.addEventListener('mouseover', () => {
					if (mapPin && mapPin.src && window.googleMapMarkerHover) {
						mapPin.setAttribute('src', sanitizeText(window.googleMapMarkerHover));
					}
				});

				mapPin.addEventListener('mouseleave', () => {
					if (mapPin && mapPin.src && window.googleMapMarkerDefault) {
						mapPin.setAttribute('src', sanitizeText(window.googleMapMarkerDefault));
					}
				});

				this.markers.push(marker);

				const infoWindow = new google.maps.InfoWindow({
					content: `
					<div class="infowindow-location" data-theme="white">
						<a class="infowindow-location__link" href="${sanitizeText(center.permalink)}">
							<h3 class="infowindow-location__title">${sanitizeText(center.name.webLabel)}</h3>
							<p class="infowindow-location__address">
								${sanitizeText(center.address.addressLines.filter((n) => n).join(', '))}<br/>
								${sanitizeText(center.address.city)}, ${sanitizeText(center.address.state)} ${sanitizeText(center.address.zip)}
							</p>
						</a>
					</div>
					`,
				});

				marker.addListener('gmp-click', () => {
					if (this.openInfoWindow) {
						this.openInfoWindow.close();
					}

					infoWindow.open(this.map, marker);
					this.openInfoWindow = infoWindow;

					window.dataLayer.push({
						event: 'map_pin_click',
						locationName: center.name.webLabel,
					});
				});
			}
		});

		this.map.fitBounds(bounds);
	}

	/**
	 * Build Location result cards
	 *
	 * Add location cards to result area.
	 *
	 * @param locations Locations for building cards.
	 */
	private buildCards(locations: { center: Center; distanceInMiles: number }[]): void {
		if (this.resultsContainer) {
			locations.forEach(({ center, distanceInMiles }) => {
				if (this.resultsContainer) {
					this.resultsContainer.innerHTML += `
					<div class="card-location-map" data-theme="white">
						<a class="card-location-map__link" href="${sanitizeText(center.permalink)}">
							<div class="card-location-map__top">
								<h3 class="card-location-map__title">${sanitizeText(center.name.webLabel)}</h3>
								<span class="card-location-map__proximity">${sanitizeText(distanceInMiles.toString())} miles</span>
							</div>
							<div class="card-location-map__bottom">
								<p class="card-location-map__address">${sanitizeText(center.address.addressLines.filter((n) => n).join(', '))}<br/>${sanitizeText(center.address.city)}, ${sanitizeText(center.address.state)} ${sanitizeText(center.address.zip)}</p>
								<span class="card-location-map__button btn-tertiary">View Location</span>
							</div>
						</a>
					</div>
				`;
				}
			});
		}
	}

	/**
	 * Recenter the map.
	 */
	private recenterMap(): void {
		this.map?.setCenter(this.initialMapCenter);
		this.map?.setZoom(this.initialMapZoom);
	}

	/**
	 * Display error in UI
	 * @param error Error string to display in UI.
	 */
	private displayError(error: string): void {
		if (this.errorContainer) {
			if (error) {
				this.errorContainer.innerHTML = sanitizeHtml(error);
				this.errorContainer.setAttribute('aria-hidden', 'false');
			} else {
				this.errorContainer.innerHTML = '';
				this.errorContainer.setAttribute('aria-hidden', 'true');
			}
		}
	}

	/**
	 * Clear errors
	 */
	private clearErrors(): void {
		if (this.errorContainer) {
			this.errorContainer.innerHTML = '';
			this.errorContainer.setAttribute('aria-hidden', 'true');
		}
	}

	/**
	 * Reset UI for new results
	 *
	 * Clear all current markers and cards.
	 */
	private resetUI(): void {
		this.markers.forEach((marker) => (marker.map = null));
		this.markers = [];
		if (this.resultsContainer) {
			this.resultsContainer.innerHTML = '';
		}
	}

	/**
	 * Validate Zipcode
	 *
	 * @param zipcode
	 * @return boolean Valid zipcode.
	 */
	private isValidZipcode(zipcode: string) {
		return /^\d{5}(-\d{4})?$/.test(zipcode);
	}
}

/**
 * Inits GoogleMapHandler after script loads.
 *
 * Set to the window object to make it available in callback function.
 */
function initMap(): void {
	if (!window.google || !window.google.maps) {
		return;
	}

	new GoogleMapHandler();
}

window.initMap = initMap;

/**
 * Append Google Maps script to page
 *
 * Issues regsitering this script in Altis locally.
 */
function loadGoogleMapsAPI(): void {
	if (!window.googleKey) {
		return;
	}

	if (window.google && window.google.maps) {
		initMap();
		return;
	}

	const script = document.createElement('script');
	script.src = `https://maps.googleapis.com/maps/api/js?key=${sanitizeText(window.googleKey)}&loading=async&callback=initMap&libraries=marker&v=beta`;
	script.async = true;
	script.defer = true;
	document.head.appendChild(script);
}

/**
 * Sanitize text from variables before use.
 *
 * @param input Text input
 * @return string Sanitized text
 */
function sanitizeText(input: string): string {
	return sanitizeHtml(input, {
		allowedTags: [],
		allowedAttributes: {},
	});
}

loadGoogleMapsAPI();

// Watch Zipcode input for dynamic label.
document.querySelectorAll('.block-location-map').forEach((block) => {
	const input = block.querySelector<HTMLInputElement>('.block-location-map__search-input');

	if (input) {
		const toggleValueClass = () => {
			input.classList.toggle('block-location-map__search-input--has-value', !!input.value);
		};

		toggleValueClass();
		input.addEventListener('input', toggleValueClass);
	}
});
